/**
 * Mirror of Massrestaurant.Core.Constants.ModuleKeys. Used by useIsModuleEnabled and
 * the route guards. Keep in sync with the backend ModuleKeys.cs — these strings are
 * the setting keys persisted in the Setting table.
 */
export const MODULE_KEYS = {
  Facturacion: 'FacturacionActiva',
  Inventario: 'InventarioActivo',
  MenuDelDia: 'MenuDelDiaActivo',
  Finanzas: 'FinanzasActiva',
  AsistenteIA: 'AsistenteIAActivo',
  MercadoPagoPoint: 'MercadoPagoPointActivo',
  AutocapturaGastos: 'AutocapturaGastosActiva',
  MenuDigital: 'MenuDigitalActivo',
  IngestaMenuIA: 'IngestaMenuIAActiva',
  RecetaTextoIA: 'RecetaTextoIAActiva',
} as const;

export type ModuleKey = (typeof MODULE_KEYS)[keyof typeof MODULE_KEYS];

/** Los tres planes de paga. Espejo de PlanNames.Paid en el backend. */
export const PAID_PLANS = ['Basico', 'Estandar', 'Profesional'] as const;

/**
 * Plan name that activates the bypass on Pro-bundle modules. Source of truth is
 * Organization.Plan (synced from Stripe), which in this codebase always stores
 * "Profesional". Match the backend PlanNames.IsProfessional helper exactly:
 * case-insensitive + trim, Spanish only.
 *
 * Desde que el trial vigente también tiene el paquete Profesional, la regla completa
 * la calcula el backend (`hasProBundle` en /organization/current-plan); esto queda
 * como fallback de useIsModuleEnabled para respuestas cacheadas sin ese campo.
 */
export function isProfessionalPlan(planName: string | undefined | null): boolean {
  if (!planName) return false;
  return planName.trim().toLowerCase() === 'profesional';
}

/**
 * Espejo de PlanNames.IsPaid. El nombre nunca puede ser el de un extra ("Usuario
 * adicional"): el backend solo escribe planes base en Organization.Plan y solo devuelve
 * planes base en subscription-status. Comparar con literales case-sensitive en cada
 * llamador era justo lo que hacía que un nombre distinto pareciera trial expirado.
 */
export function isPaidPlan(planName: string | undefined | null): boolean {
  if (!planName) return false;
  const normalized = planName.trim().toLowerCase();
  return PAID_PLANS.some((p) => p.toLowerCase() === normalized);
}

const PLAN_DISPLAY_NAMES: Record<string, string> = {
  basico: 'Básico',
  estandar: 'Estándar',
  profesional: 'Profesional',
  trial: 'de prueba',
};

/** Nombre del plan para mostrar ("Basico" → "Básico"); el que no conoce lo deja tal cual. */
export function planDisplayName(planName: string | undefined | null): string {
  if (!planName) return '';
  return PLAN_DISPLAY_NAMES[planName.trim().toLowerCase()] ?? planName;
}

/** WhatsApp de soporte de Massrestaurant. */
export const SUPPORT_WHATSAPP_URL = 'https://wa.me/522206294774';

/** Suscripción abre la compra de usuarios adicionales al cargar con este parámetro. */
export const BUY_EXTRA_USERS_PARAM = 'comprar';
export const BUY_EXTRA_USERS_VALUE = 'usuarios';
export const BUY_EXTRA_USERS_PATH = `/subscription?${BUY_EXTRA_USERS_PARAM}=${BUY_EXTRA_USERS_VALUE}`;
